export const NAME_MIN = 2;
export const NAME_MAX = 100;

const HAS_LETTER = /[A-Za-z]/;

const NAME_ALLOWED = /^[A-Za-z0-9 .,'&()/+%:_-]+$/;

const MOBILE_PATTERN = /^[0-9]{10}$/;
const WEBSITE_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[A-Za-z]{2,}(\/\S*)?$/;
const PAN_PATTERN = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
const GSTIN_PATTERN =
  /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z]Z[0-9A-Za-z]$/;

const trimmed = (value) => String(value ?? "").trim();

export const nameRules = (label, { required = true, maxLength = NAME_MAX } = {}) => {
  return [
    ...(required
      ? [
          {
            required: true,
            message: `Please enter ${label.toLowerCase()} name`,
            transform: trimmed,
          },
        ]
      : []),
    {
      validator: (_, value) => {
        const text = trimmed(value);
        if (!text) return Promise.resolve(); 

        if (text.length < NAME_MIN) {
          return Promise.reject(
            new Error(`Name must be at least ${NAME_MIN} characters`)
          );
        }
        if (maxLength && text.length > maxLength) {
          return Promise.reject(
            new Error(`Name cannot exceed ${maxLength} characters`)
          );
        }
        if (!HAS_LETTER.test(text)) {
          return Promise.reject(
            new Error("Name cannot be only numbers or symbols")
          );
        }
        if (!NAME_ALLOWED.test(text)) {
          return Promise.reject(
            new Error(
              "Name can use letters, numbers, spaces and . , ' & ( ) / + % : - only"
            )
          );
        }
        return Promise.resolve();
      },
    },
  ];
};


export const positiveIntegerRules = (label) => [
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const text = String(value).trim();
      if (!/^\d+$/.test(text) || Number(text) < 1) {
        return Promise.reject(
          new Error(`${label} must be a postive integer`)
        );
      }
      return Promise.resolve();
    },
  },
];

export const HSN_MIN = 4;
export const HSN_MAX = 15;

export const hsnRules = ({ required = false } = {}) => [
  ...(required
    ? [{ required: true, message: "Please enter the code", transform: trimmed }]
    : []),
  {
    validator: (_, value) => {
      const text = trimmed(value);
      if (!text) return Promise.resolve();
      if (!/^[0-9]+$/.test(text)) {
        return Promise.reject(new Error("HSN/SAC can contain numbers only"));
      }
      if (text.length < HSN_MIN || text.length > HSN_MAX) {
        return Promise.reject(
          new Error(`HSN/SAC must be ${HSN_MIN} to ${HSN_MAX} digits`)
        );
      }
      return Promise.resolve();
    },
  },
];

/** Short codes are alphanumeric, no punctuation. */
export const shortCodeRules = [
  {
    validator: (_, value) => {
      const text = trimmed(value);
      if (!text) return Promise.resolve();
      if (!/^[A-Za-z0-9]+$/.test(text)) {
        return Promise.reject(
          new Error("Short code can contain letters and numbers only")
        );
      }
      if (text.length > 20) {
        return Promise.reject(new Error("Short code cannot exceed 20 characters"));
      }
      return Promise.resolve();
    },
  },
];

const optionalPatternRule = (pattern, message) => ({
  validator: (_, value) => {
    const text = trimmed(value);
    if (!text) return Promise.resolve();
    return pattern.test(text)
      ? Promise.resolve()
      : Promise.reject(new Error(message));
  },
});

export const mobileRules = [
  optionalPatternRule(MOBILE_PATTERN, "Enter a valid 10 digit mobile number"),
];

export const websiteRules = [
  optionalPatternRule(WEBSITE_PATTERN, "Enter a valid website"),
];

export const panRules = [
  optionalPatternRule(PAN_PATTERN, "Enter a valid PAN"),
];

export const gstinRules = [
  optionalPatternRule(GSTIN_PATTERN, "Enter a valid 15 character GSTIN"),
];

/**
 * A selling rate may never exceed the MRP printed on the pack. Used on every
 * rate field that sits alongside an MRP - purchase entry, batch edit and the
 * medicine master - so the rule is enforced wherever a rate is entered, not
 * only at the till.
 *
 * `mrpField` names the MRP field in the same form; pass the form instance so the
 * comparison always reads the value currently on screen.
 */
export const rateNotAboveMrpRules = (form, label, mrpField = "mrp") => [
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const mrp = Number(form?.getFieldValue?.(mrpField));
      if (!Number.isFinite(mrp) || mrp <= 0) return Promise.resolve();

      if (Number(value) > mrp) {
        return Promise.reject(
          new Error(`${label} cannot be more than the MRP (${mrp})`)
        );
      }
      return Promise.resolve();
    },
  },
];


export const rateNotBelowPurchaseRules = (
  form,
  label,
  purchaseField = "purchase_rate"
) => [
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const purchase = Number(form?.getFieldValue?.(purchaseField));
      if (!Number.isFinite(purchase) || purchase <= 0) return Promise.resolve();

      if (Number(value) < purchase) {
        return Promise.reject(
          new Error(`${label} cannot be less than the purchase rate (${purchase})`)
        );
      }
      return Promise.resolve();
    },
  },
];

export const loosePriceRules = (
  form,
  { mrpField = "mrp", conversionField = "strip_conversion" } = {}
) => [
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const mrp = Number(form?.getFieldValue?.(mrpField));
      const conversion = Number(form?.getFieldValue?.(conversionField));
      if (!Number.isFinite(mrp) || mrp <= 0) return Promise.resolve();
      if (!Number.isFinite(conversion) || conversion <= 0) {
        return Promise.resolve();
      }

      const ceiling = Math.round((mrp / conversion) * 100) / 100;
      if (Number(value) > ceiling) {
        return Promise.reject(
          new Error(
            `Loose price cannot be more than ${ceiling}`
          )
        );
      }
      return Promise.resolve();
    },
  },
];


export const rateAboveMrpError = (rate, mrp, label = "Purchase rate") => {
  const value = Number(rate);
  const ceiling = Number(mrp);
  if (!Number.isFinite(value) || value <= 0) return null;
  /* No MRP on file yet is not a breach - there is nothing to measure against. */
  if (!Number.isFinite(ceiling) || ceiling <= 0) return null;
  if (value > ceiling) {
    return `${label} cannot be more than the MRP (${ceiling})`;
  }
  return null;
};

/*
 * Money that has to be worth something.
 *
 * An MRP, a rate or a selling price of nought is not a cheap medicine, it is
 * a medicine nobody priced - and it travels: the batch prices the line, the
 * line prices the bill, and a zero at the start comes out as a bill charging
 * nothing at all. A negative one is not a price in any reading.
 *
 * So every one of these fields is held above zero rather than at or above it,
 * on the form and on the stepper both: `MONEY_MIN` is what the boxes clamp
 * to, and these rules are what refuse a typed nought before it is saved.
 *
 * Quantities and discounts are deliberately not covered - nought of either is
 * an ordinary thing to mean.
 */
export const MONEY_MIN = 0.01;

export const moneyRules = (label, { required = false } = {}) => [
  /* The label as it is written - "Rate A", not "rate a". */
  ...(required ? [{ required: true, message: `Please enter ${label}` }] : []),
  {
    validator: (_, value) => {
      /* Left empty is the required rule's business, not this one's. */
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      const amount = Number(value);
      if (!Number.isFinite(amount)) {
        return Promise.reject(new Error(`${label} must be a number`));
      }
      if (amount <= 0) {
        return Promise.reject(new Error(`${label} must be greater than 0`));
      }
      return Promise.resolve();
    },
  },
];

/*
 * The same rule as `moneyRules`, in the form the line grids want it.
 *
 * A grid cell has no antd Form behind it - it prints its own message under
 * the box - so the rule is also offered as a plain string-or-null, the way
 * `rateAboveMrpError` already is, and the two read side by side on a line.
 */
export const notPositiveError = (value, label = "Rate") => {
  if (value === undefined || value === null || value === "") return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `${label} must be a number`;
  if (amount <= 0) return `${label} must be greater than 0`;
  return null;
};

export const RATE_FIELDS = [
  "selling_price",
  "purchase_rate",
  "loose_price",
  "rate_a",
  "rate_b",
  "rate_c",
];

/*
 * The one shape a GST rate is allowed to take, anywhere in the module.
 *
 * GST is levied once and written down two ways: within a state it is split in
 * half between the centre and the state, across state lines it is charged
 * whole as IGST. So two things are always true of a medicine, a batch or a
 * line that carries a rate -
 *
 *   CGST = SGST                 the split is a half each, never 3 and 4
 *   SGST + CGST = IGST          the two spellings are the same tax
 *
 * - and a record where they are not true is not a rate anyone can charge, it
 * is a typo. Both halves of the pair are marked when they disagree, because
 * either one of them could be the wrong one and the form cannot know which.
 *
 * Kept here rather than in each form so every screen that takes a rate asks
 * the same question of it and says the same thing when the answer is no.
 */
export const GST_RATE_FIELDS = ["sgst", "cgst", "igst"];

const GST_LABEL = { sgst: "SGST", cgst: "CGST", igst: "IGST" };

/* Rates are typed to a decimal or two, so the two sides of an equals sign are
   compared at the precision they are written to rather than exactly. */
const sameRate = (a, b) => Math.abs(a - b) < 0.005;

const rateGiven = (value) => value !== undefined && value !== null && value !== "";

const asRate = (value) => (rateGiven(value) ? Number(value) : 0);

/**
 * @param form   the antd form instance holding the three fields
 * @param field  which of sgst / cgst / igst these rules are for
 */
export const gstRateRules = (form, field) => [
  {
    validator: (_, value) => {
      if (!rateGiven(value)) return Promise.resolve();
      const rate = Number(value);
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        return Promise.reject(
          new Error(`${GST_LABEL[field]} must be between 0 and 100`)
        );
      }
      return Promise.resolve();
    },
  },
  {
    validator: (_, value) => {
      /* The field being judged is not on the form yet - it is in hand. */
      const read = (name) => (name === field ? value : form.getFieldValue(name));
      const raw = GST_RATE_FIELDS.map(read);

      /* A record with no rate on it at all is a record that has not been
         filled in, not one that has been filled in wrongly. */
      if (!raw.some(rateGiven)) return Promise.resolve();

      const [sgst, cgst, igst] = raw.map(asRate);
      if (![sgst, cgst, igst].every(Number.isFinite)) return Promise.resolve();

      if (field !== "igst" && !sameRate(sgst, cgst)) {
        return Promise.reject(new Error("CGST and SGST must be equal"));
      }
      if (!sameRate(sgst + cgst, igst)) {
        return Promise.reject(
          new Error(
            `IGST must equal SGST + CGST (${Number((sgst + cgst).toFixed(2))}%)`
          )
        );
      }
      return Promise.resolve();
    },
  },
];

/**
 * Re-ask the question of the other two boxes.
 *
 * Each of the three is judged against the other two, so a mismatch typed away
 * in one of them leaves a stale error standing on the others until they are
 * asked again.
 */
export const revalidateGstRates = (form) => {
  form.validateFields(GST_RATE_FIELDS).catch(() => {});
};
